import { c } from 'ttag';

import { refusalCode } from '../../graphql/refusalCode';
import { Refusal } from '../accounts/portalAccess';

/**
 * Lecture des refus de `updateMembershipPerimeter` (PCG-357, mutation livrée à PCG-305).
 *
 * Les codes que le service peut lever : MEMBERSHIP_NOT_FOUND, MISSING_PERMISSION,
 * FORBIDDEN_OUT_OF_SCOPE, UNKNOWN_MARKET_SCOPE, EMPTY_MARKET_RESTRICTION,
 * MARKETS_WITH_UNRESTRICTED_SCOPE, MARKET_NOT_IN_SCOPE. La lecture du code sur l'erreur
 * n'est pas recopiée : elle reste dans `refusalCode`.
 *
 * L'écran évite déjà EMPTY_MARKET_RESTRICTION et MARKETS_WITH_UNRESTRICTED_SCOPE en gérant
 * la portée et la sélection avant l'envoi : ils sont attrapés au cas où la mutation soit
 * forcée, ou qu'une course laisse passer un état incohérent.
 *
 * MARKET_NOT_IN_SCOPE n'est atteignable ici que si une activité a disparu du compte entre
 * l'ouverture du dialog et l'envoi : le message renvoie donc à un rechargement.
 */
export const perimeterRefusalOf = (error: unknown): Refusal => {
    switch (refusalCode(error)) {
        case 'MISSING_PERMISSION':
            return {
                title: c('Perimeter refusal').t`Your role does not let you manage users.`,
                guidance: c('Perimeter refusal')
                    .t`Setting a perimeter is a user-administration act. Ask an administrator, or have your role widened.`,
            };
        case 'FORBIDDEN_OUT_OF_SCOPE':
            return {
                title: c('Perimeter refusal').t`This account is outside your portfolio.`,
                guidance: c('Perimeter refusal')
                    .t`You may not manage users on it. Someone with the wider perimeter must set it.`,
            };
        case 'EMPTY_MARKET_RESTRICTION':
            return {
                title: c('Perimeter refusal').t`A restricted perimeter needs at least one activity.`,
                guidance: c('Perimeter refusal')
                    .t`With nothing ticked the person would see nothing. Tick some activities, or grant them the whole account.`,
            };
        case 'MARKETS_WITH_UNRESTRICTED_SCOPE':
            return {
                title: c('Perimeter refusal').t`The whole account cannot carry a list of activities.`,
                guidance: c('Perimeter refusal')
                    .t`Grant the whole account, or restrict it and tick the activities. Not both.`,
            };
        case 'MARKET_NOT_IN_SCOPE':
            return {
                title: c('Perimeter refusal').t`One of the activities no longer belongs to this account.`,
                guidance: c('Perimeter refusal')
                    .t`It may have just moved or vanished. Close this and reopen it to read the account as it stands.`,
            };
        case 'MEMBERSHIP_NOT_FOUND':
            return {
                title: c('Perimeter refusal').t`This attachment no longer exists.`,
                guidance: c('Perimeter refusal')
                    .t`The person may have just been detached from this account. Go back to the sheet to read it as it stands.`,
            };
        default:
            return {
                title: c('Perimeter refusal').t`The perimeter could not be saved.`,
                guidance: c('Perimeter refusal')
                    .t`Nothing was changed. If it keeps refusing, pass the sentence below on to your Freelance.com contact.`,
            };
    }
};
